import { useEffect, useCallback, useRef, useState } from "react";
import { PSAuthConfig } from "@pineseed/workspace/handlers/auth-handler";
import type { ChannelType, SubscriptionParams } from "@pineseed/spec";

export interface UseAdminAppProps {
  pineSeedAuth: PSAuthConfig;
  httpUrl: string;
  wsUrl: string;
  isConnected: boolean;
}

export interface UseAdminAppReturn {
  httpUrl: string;
  wsUrl: string;
  workspaceId: string;
  setWorkspaceId: (id: string) => void;
  namespace: string;
  setNamespace: (namespace: string) => void;
  isConnected: boolean;

  // Generic API methods — workspace context is included by default; pass skipWorkspace for platform-scoped calls
  fetchEntities: <T>(endpoint: string, params?: Record<string, string>, options?: { skipWorkspace?: boolean }) => Promise<T[]>;
  createEntity: <T>(endpoint: string, data: unknown, workspaceId?: string, options?: { skipWorkspace?: boolean }) => Promise<T>;
  updateEntity: <T>(endpoint: string, id: string, data: unknown, workspaceId?: string, options?: { skipWorkspace?: boolean }) => Promise<T>;
  deleteEntity: (endpoint: string, id: string, workspaceId?: string, options?: { skipWorkspace?: boolean }) => Promise<void>;

  // WebSocket subscription for real-time updates
  subscribeToChannel: (channel: string, handler: (data: unknown) => void) => () => void;

  // Managed subscriptions
  subscribe: (channelType: ChannelType, channelName: string, params: SubscriptionParams) => void;
  unsubscribe: (channelName: string) => void;
  activeSubscriptions: Set<string>;
}

export function useAdminApp({
  httpUrl,
  wsUrl,
  isConnected,
}: UseAdminAppProps): UseAdminAppReturn {
  const channelHandlersRef = useRef<Map<string, Set<(data: unknown) => void>>>(new Map());

  // Workspace selection state - admin can switch between workspaces
  // Load from localStorage or use default
  const [workspaceId, setWorkspaceId] = useState<string>(() => {
    const stored = localStorage.getItem('admin.workspaceId');
    return stored || '';
  });

  // Persist workspace ID to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('admin.workspaceId', workspaceId);
  }, [workspaceId]);

  // Global namespace state - admin can set namespace for tag operations
  // Load from localStorage or use default
  const [namespace, setNamespace] = useState<string>(() => {
    const stored = localStorage.getItem('admin.namespace');
    return stored || 'general';
  });

  // Persist namespace to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('admin.namespace', namespace);
  }, [namespace]);

  // Active subscriptions tracking (using ref to avoid stale closures in WebSocket callbacks)
  const [activeSubscriptions, setActiveSubscriptions] = useState<Set<string>>(new Set());
  const activeSubscriptionsRef = useRef<Set<string>>(new Set());

  // Sync activeSubscriptionsRef with state
  useEffect(() => {
    activeSubscriptionsRef.current = activeSubscriptions;
  }, [activeSubscriptions]);

  // Generic fetch entities
  const fetchEntities = useCallback(async <T,>(
    endpoint: string,
    params?: Record<string, string>,
    options?: { skipWorkspace?: boolean }
  ): Promise<T[]> => {
    const useWs = !options?.skipWorkspace && workspaceId;
    const queryParams = new URLSearchParams({
      ...(useWs && { workspaceId }),
      ...params,
    });

    const response = await fetch(`${httpUrl}/api/${endpoint}?${queryParams}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(useWs && { 'X-Workspace-Id': workspaceId }),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${endpoint}: ${response.statusText}`);
    }

    const data = await response.json();

    // Handle different response formats
    if (Array.isArray(data)) {
      return data as T[];
    } else if (data.tags) {
      return data.tags as T[];
    } else if (data.participants) {
      return data.participants as T[];
    } else if (data.messages) {
      return data.messages as T[];
    } else if (data.threads) {
      return data.threads as T[];
    }

    return [];
  }, [httpUrl, workspaceId]);

  // Generic create entity
  const createEntity = useCallback(async <T,>(
    endpoint: string,
    data: unknown,
    targetWorkspaceId?: string,
    options?: { skipWorkspace?: boolean }
  ): Promise<T> => {
    const wsId = options?.skipWorkspace ? '' : (targetWorkspaceId || workspaceId);
    const payload = typeof data === 'object' && data !== null
      ? { ...data as Record<string, unknown>, ...(wsId && { workspaceId: wsId }) }
      : { ...(wsId && { workspaceId: wsId }) };

    const response = await fetch(`${httpUrl}/api/${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(wsId && { 'X-Workspace-Id': wsId }),
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      console.error(`❌ API Error [POST ${endpoint}]:`, {
        status: response.status,
        statusText: response.statusText,
        body: errorBody,
      });
      throw new Error(`Failed to create ${endpoint}: ${response.statusText}`);
    }

    return await response.json() as T;
  }, [httpUrl, workspaceId]);

  // Generic update entity
  const updateEntity = useCallback(async <T,>(
    endpoint: string,
    id: string,
    data: unknown,
    targetWorkspaceId?: string,
    options?: { skipWorkspace?: boolean }
  ): Promise<T> => {
    const wsId = options?.skipWorkspace ? '' : (targetWorkspaceId || workspaceId);

    // Try URL path pattern first (e.g., /tags/:id)
    let response = await fetch(`${httpUrl}/api/${endpoint}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(wsId && { 'X-Workspace-Id': wsId }),
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    // If 404, try body pattern (e.g., /participant with id in body)
    if (response.status === 404) {
      const payload = typeof data === 'object' && data !== null
        ? { id, ...(wsId && { workspaceId: wsId }), ...data as Record<string, unknown> }
        : { id, ...(wsId && { workspaceId: wsId }) };

      response = await fetch(`${httpUrl}/api/${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(wsId && { 'X-Workspace-Id': wsId }),
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      console.error(`❌ API Error [PUT ${endpoint}/${id}]:`, {
        status: response.status,
        statusText: response.statusText,
        body: errorBody,
      });
      throw new Error(`Failed to update ${endpoint}: ${response.statusText}`);
    }

    return await response.json() as T;
  }, [httpUrl, workspaceId]);

  // Generic delete entity
  const deleteEntity = useCallback(async (
    endpoint: string,
    id: string,
    targetWorkspaceId?: string,
    options?: { skipWorkspace?: boolean }
  ): Promise<void> => {
    const wsId = options?.skipWorkspace ? '' : (targetWorkspaceId || workspaceId);

    // Try URL path pattern first (e.g., /tags/:id)
    let response = await fetch(`${httpUrl}/api/${endpoint}/${id}${wsId ? `?workspaceId=${wsId}` : ''}`, {
      method: 'DELETE',
      headers: {
        ...(wsId && { 'X-Workspace-Id': wsId }),
      },
      credentials: 'include',
    });

    // If 404, try query param pattern (e.g., /participant?id=xxx)
    if (response.status === 404) {
      response = await fetch(`${httpUrl}/api/${endpoint}?id=${id}${wsId ? `&workspaceId=${wsId}` : ''}`, {
        method: 'DELETE',
        headers: {
          ...(wsId && { 'X-Workspace-Id': wsId }),
        },
        credentials: 'include',
      });
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      console.error(`❌ API Error [DELETE ${endpoint}/${id}]:`, {
        status: response.status,
        statusText: response.statusText,
        body: errorBody,
      });
      throw new Error(`Failed to delete ${endpoint}: ${response.statusText}`);
    }
  }, [httpUrl, workspaceId]);

  // Subscribe to WebSocket channel (internal - used by hooks)
  const subscribeToChannel = useCallback((
    channel: string,
    handler: (data: unknown) => void
  ): (() => void) => {
    console.log(`🔵 [SystemAdmin] subscribeToChannel called:`, channel);

    // Add handler to map
    if (!channelHandlersRef.current.has(channel)) {
      channelHandlersRef.current.set(channel, new Set());
    }
    channelHandlersRef.current.get(channel)!.add(handler);

    // Track subscription for reconnection — only update state if channel is new
    setActiveSubscriptions((prev) => {
      if (prev.has(channel)) return prev;
      const updated = new Set(prev);
      updated.add(channel);
      return updated;
    });

    // Return unsubscribe function
    return () => {
      const handlers = channelHandlersRef.current.get(channel);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          console.log(`🔴 [SystemAdmin] Last handler removed, unsubscribing from channel:`, channel);
          channelHandlersRef.current.delete(channel);

          // Remove from active subscriptions when no handlers left
          setActiveSubscriptions((prev) => {
            if (!prev.has(channel)) return prev;
            const updated = new Set(prev);
            updated.delete(channel);
            return updated;
          });
        } else {
          console.log(`🔵 [SystemAdmin] Handler removed but ${handlers.size} handlers remain for channel:`, channel);
        }
      }
    };
  }, []);

  // Managed subscription (for UI control)
  const subscribe = useCallback((
    channelType: ChannelType,
    channelName: string,
    params: SubscriptionParams
  ) => {
    console.log(`🟢 [SystemAdmin] Subscribing to ${channelType}:`, channelName, params);

    // Check if already subscribed
    if (activeSubscriptions.has(channelName)) {
      console.log(`⚠️ [SystemAdmin] Already subscribed to:`, channelName);
      return;
    }

    // Track active subscription
    setActiveSubscriptions((prev) => {
      const updated = new Set(prev);
      updated.add(channelName);
      return updated;
    });
  }, [activeSubscriptions]);

  // Unsubscribe from channel
  const unsubscribe = useCallback((channelName: string) => {
    console.log(`🔴 [SystemAdmin] Unsubscribing from:`, channelName);

    // Remove from active subscriptions
    setActiveSubscriptions((prev) => {
      const updated = new Set(prev);
      updated.delete(channelName);
      return updated;
    });

    // Note: We don't send unsubscribe to server as the backend doesn't support it yet
    // The subscription will remain active on the server side
  }, []);

  return {
    httpUrl,
    wsUrl,
    workspaceId,
    setWorkspaceId,
    namespace,
    setNamespace,
    isConnected,
    fetchEntities,
    createEntity,
    updateEntity,
    deleteEntity,
    subscribeToChannel,
    subscribe,
    unsubscribe,
    activeSubscriptions,
  };
}

export { useAdminApp as default };
